package ai.jessie.web.pages.organisations

import ai.jessie.web.invoices.INVOICES_TITLE
import ai.jessie.web.invoices.InvoiceState
import ai.jessie.web.invoices.LocalInvoicePort
import ai.jessie.web.invoices.invoiceRecords
import ai.jessie.web.invoices.invoiceTitle
import ai.jessie.web.invoices.statusBadge
import ai.jessie.web.onboarding.LocalOnboardingPort
import ai.jessie.web.onboarding.OnboardingProblem
import ai.jessie.web.onboarding.onboardingProblem
import ai.jessie.web.onboarding.organisationRequiredProblem
import ai.jessie.web.onboarding.validOrganisationId
import ai.jessie.web.theme.AlertStyle
import ai.jessie.web.theme.ButtonVariant
import ai.jessie.web.theme.appFrame
import ai.jessie.web.theme.crumb
import ai.jessie.web.theme.themedButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.key
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import com.varabyte.kobweb.core.AppGlobals
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.PageContext
import com.varabyte.kobweb.core.isExporting
import com.varabyte.kobweb.silk.style.toAttrs
import org.jetbrains.compose.web.dom.Div

// UC-013. Tenant state is keyed by the routed organisation, so a change of organisation discards
// it together with any unsaved draft invoice (A11).
@Page("{organisationId}/invoices")
@Composable
fun organisationInvoicesPage(ctx: PageContext) {
    val organisationId = ctx.route.dynamicParams["organisationId"].orEmpty()
    if (!validOrganisationId(organisationId)) {
        invoiceProblemFrame(organisationId, organisationRequiredProblem())
    } else key(organisationId) { invoicesContent(organisationId, null) }
}

/** The invoice frame around a problem that needs no request, e.g. an invalid link. */
@Composable
internal fun invoiceProblemFrame(organisationId: String, problem: OnboardingProblem) {
    appFrame(organisationId, invoicesHref(organisationId), INVOICES_TITLE) {
        Div(attrs = AlertStyle.toAttrs()) { onboardingProblem(problem, busy = false) }
    }
}

// The top bar has no invoice entry, so `current` marks none. The title names the shown view; the
// view itself moves focus (`invoiceRecords`), so the frame focuses its title only once.
@Composable
internal fun invoicesContent(organisationId: String, invoiceId: String?) {
    val port = LocalInvoicePort.current
    val role = LocalOnboardingPort.current.startedRole(organisationId)
    val scope = rememberCoroutineScope()
    val model =
        remember(port, scope, organisationId, invoiceId) {
            InvoiceState(port, scope, organisationId, invoiceId)
        }
    LaunchedEffect(model) { if (!AppGlobals.isExporting) model.refresh() }
    val state = model.state
    val authenticationRequired = state.error?.authenticationRequired == true
    val title = invoiceTitle(invoiceId, state)
    val record = state.current?.takeIf { state.draft == null }
    // UC-015 B6: creation is offered to the roles with FINANCE_MANAGE; the server decides.
    val creatable =
        invoiceId == null &&
            state.draft == null &&
            record == null &&
            state.invoices != null &&
            !authenticationRequired &&
            (role == "OWNER" || role == "STANDARD")
    appFrame(
        organisationId,
        invoicesHref(organisationId),
        title,
        subtitle = record?.recipientName,
        authenticationRequired = authenticationRequired,
        crumb =
            if (invoiceId != null) {
                { crumb(INVOICES_TITLE, invoicesHref(organisationId), record?.number ?: title) }
            } else null,
        badge = record?.let { { statusBadge(it.status) } },
        actions =
            if (creatable) {
                {
                    themedButton(
                        "Rechnung erstellen",
                        model.actions.create,
                        variant = ButtonVariant.PRIMARY,
                        disabled = state.busy,
                    )
                }
            } else null,
        refocusTitle = false,
    ) {
        invoiceRecords(organisationId, invoiceId, state, model.actions)
    }
}

internal fun invoicesHref(organisationId: String) = "/organisations/$organisationId/invoices"
